import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import { handleTreatmentServiceNavigation } from '../components/treatmentNavigationHelper';
import styles from '../TreatmentTypes/css/BloodPressure.module.css';
import newStyle from './css/DiscoveryVitality.module.css';
import { PersonStanding, Send } from 'lucide-react';

const DiscoveryVitality = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim();
    const profileNumber = patient?.profilenumber;
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase();

    // Vitals capture states
    const [glucose, setGlucose] = useState('');
    const [cholesterol, setCholesterol] = useState('');
    const [bpSystolic, setBpSystolic] = useState('');
    const [bpDiastolic, setBpDiastolic] = useState('');
    
    // Fitness metrics states
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [bmi, setBmi] = useState('');
    const [waistCircumference, setWaistCircumference] = useState('');
    
    // Status states
    const [submissionStatus, setSubmissionStatus] = useState('');
    
    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');

    // Calculate BMI when weight or height changes
    useEffect(() => {
        if (weight && height) {
            const weightInKg = parseFloat(weight);
            const heightInM = parseFloat(height) / 100; // Convert cm to m
            const calculatedBmi = (weightInKg / (heightInM * heightInM)).toFixed(1);
            setBmi(calculatedBmi);
        } else {
            setBmi('');
        }
    }, [weight, height]);

    const handleInvoice = () => {
        handleTreatmentServiceNavigation({
            location,
            navigate,
            consultationData,
            updateConsultationData,
            getStepDraft,
            setStepDraft,
            serviceData: {
                discovery_vitality: {
                    glucose,
                    cholesterol,
                    bpSystolic,
                    bpDiastolic,
                    weight,
                    height,
                    bmi,
                    waistCircumference,
                    submissionStatus,
                    clinicalNotes,
                },
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <PersonStanding />
                        </div>
                        <h1 className={newStyle.title}>Discovery Vitality</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials || 'TN'}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName || 'Thandiwe Ndlovu'}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber || '448896'}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>VITALS CAPTURE</div>
                        <div className={newStyle.readingsRow1}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Glucose</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={glucose}
                                        onChange={(e) => setGlucose(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>mmol/L</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Cholesterol</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={cholesterol}
                                        onChange={(e) => setCholesterol(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>mmol/L</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>BP (Systolic)</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={bpSystolic}
                                        onChange={(e) => setBpSystolic(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>mmHG</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>BP (Diastolic)</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={bpDiastolic}
                                        onChange={(e) => setBpDiastolic(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>mmHG</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>FITNESS METRICS</div>
                        <div className={newStyle.readingsRow}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Weight</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={weight}
                                        onChange={(e) => setWeight(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>kg</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Height</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) => setHeight(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>cm</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>BMI (auto-calculated)</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={bmi}
                                        onChange={(e) => setBmi(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Waist Circumference</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={waistCircumference}
                                        onChange={(e) => setWaistCircumference(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>cm</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>STATUS</div>
                        <div className={newStyle.readingsRow2}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Submission to medical aid</label>
                                <div className={styles.inputGroup}>
                                    <Dropdown
                                        name="submissionStatus"
                                        value={submissionStatus}
                                        onChange={setSubmissionStatus}
                                        options={['Submitted', 'Pending', 'Rejected', 'Approved']}
                                        placeholder="Select..."
                                        className={newStyle.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={styles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={styles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={styles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default DiscoveryVitality;